import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingOverlayMessage from 'features/queue/grid/loadingOverlayMessage';
import { selectIsNavBarCollapsed, selectIsNavBarHidden } from 'features/loggedInRoutesPanel/slice';
import { FloatingTicketInstanceActions, FloatingTicketInstanceModes } from '../floatingTicketPanelManager/modes';
import { StyledFloatingTicketPanelInstance } from './style';
import { StyledLoader, StyledLoaderFooter, StyledLoaderHeader } from '../style';
import { indexOnScreen, getWantedWidth, calculateRight, getVisiblePanelsOnScreenCount } from '../utils/utils';
import { CONSTANTS } from '../utils/constants';

const { COLLAPSED_MIN_WIDTH, OPENED_MAX_WIDTH, RIGHT_BUFFER, DEFAULT_RIGHT_BUFFER } = CONSTANTS;

export function FloatingTicketPanelInstance(props) {
  const {
    url,
    mode,
    index,
    startIndexToShow,
    openedTicketPanelIndex,
    expandedTicketPanelIndex,
    totalPanels,
    isFrameDataLoading,
    isRightArrowShown,
  } = props;
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [navBarWidth, setNavBarWidth] = useState(0);
  const iframeRef = useRef();
  const isNavBarCollapsed = useSelector(selectIsNavBarCollapsed);
  const isNavBarHidden = useSelector(selectIsNavBarHidden);

  useEffect(() => {
    if (!isNavBarHidden) {
      const navBarWidth = document.getElementById('nav-bar')?.offsetWidth;
      setNavBarWidth(navBarWidth);
    } else {
      setNavBarWidth(0);
    }
  }, [isNavBarHidden, isNavBarCollapsed]);

  const panelsOnScreen = getVisiblePanelsOnScreenCount(navBarWidth);

  const right = calculateRight(
    index,
    startIndexToShow,
    openedTicketPanelIndex,
    expandedTicketPanelIndex,
    panelsOnScreen,
    totalPanels,
    navBarWidth,
  );

  const { wantedCollapsedWidth, wantedExpandedOrOpenedWidth } = getWantedWidth(
    openedTicketPanelIndex,
    expandedTicketPanelIndex,
    totalPanels,
    navBarWidth,
  );

  const isOnScreen = indexOnScreen(index, panelsOnScreen);

  const style = {
    transition: 'right 0.4s ease, top 0.4s ease, opacity 0.4s ease',
    position: 'fixed',
    minWidth: COLLAPSED_MIN_WIDTH,
    bottom: 0,
    right: `${right}px`,
    opacity: isOnScreen ? 1 : 0,
    zIndex: 9999,
  };

  if (mode === FloatingTicketInstanceModes.COLLAPSED) {
    style.width = `${wantedCollapsedWidth}px`;
    style.minWidth = COLLAPSED_MIN_WIDTH;
    style.height = '48px';
  } else if (mode === FloatingTicketInstanceModes.OPENED) {
    style.height = '73.16vh';
    style.width = `${wantedExpandedOrOpenedWidth}px`;
    style.maxWidth = `${OPENED_MAX_WIDTH}px`;
  } else if (mode === FloatingTicketInstanceModes.EXPANDED) {
    style.height = '100%';
    style.position = 'fixed';
    style.left = '0';
    style.right = '0';
    style.top = '0';
    style.bottom = '0';
    style.margin = 'auto';
    style.padding = '26px 0';
    style.zIndex = 1000;
    style.width = `${OPENED_MAX_WIDTH}px`;
    style.inset = '0px';
  } else {
    throw new Error('Unsupported instance mode!');
  }

  style.right = `calc(${style.right} + ${isRightArrowShown ? RIGHT_BUFFER : DEFAULT_RIGHT_BUFFER}px)`;

  useEffect(() => {
    iframeRef.current.contentWindow.postMessage(
      {
        url,
        action: FloatingTicketInstanceActions.RequestModeChange,
        data: mode,
        target: 'iframe',
      },
      '*',
    );
  }, [mode, url]);

  useEffect(() => {
    iframeRef.current.contentWindow.postMessage(
      {
        url,
        action: FloatingTicketInstanceActions.CollapsedPanelWidthChange,
        data: wantedCollapsedWidth,
        target: 'iframe',
      },
      '*',
    );
  }, [mode, url, wantedCollapsedWidth]);

  const showLoader = !iframeLoaded || isFrameDataLoading !== false;

  return (
    <StyledFloatingTicketPanelInstance style={style} isCollapsed={mode === FloatingTicketInstanceModes.COLLAPSED}>
      <div
        className="loader"
        style={{
          ...style,
          zIndex: showLoader ? 100 : -1,
          opacity: showLoader ? 1 : 0,
        }}
      >
        <StyledLoaderHeader />
        <StyledLoader>
          <LoadingOverlayMessage className="floating-ticket" />
        </StyledLoader>
        <StyledLoaderFooter />
      </div>
      <iframe
        ref={iframeRef}
        src={url}
        onLoad={() => setIframeLoaded(true)}
        style={{
          transition: 'opacity 0.4s ease',
          width: '100%',
          height: style.height,
          position: 'relative',
          zIndex: showLoader ? -1 : 100,
          borderRadius: mode === FloatingTicketInstanceModes.EXPANDED ? '10px' : '10px 10px 0px 0px',
        }}
        title={url}
        id={`floating_panel_${index}`}
        data-testid={`floating_panel_${index}`}
        data-cy={`floating_panel_${index}`}
      />
    </StyledFloatingTicketPanelInstance>
  );
}
